/**
 * ТЗ 0074 — LMS access suspension: the pure layer behind the «Wstrzymany
 * dostęp» tab on /debtors and the block dialog on the student card.
 *
 * The backend sends machine codes only (`source`, `skipReason`, notification
 * `status` / `skipReason`, robot `stats` JSON). This module is the single place
 * that knows what each code MEANS on screen, so the tab, the dialog and the
 * history list cannot drift apart. No React, no Apollo, no i18n — it returns
 * label descriptors (`{ key, values }` or `{ text }`) that the component
 * resolves with `t`. The locale gate (`lmsSuspension.locale.test.ts`) walks the
 * key tables below, so a code added here without a translation fails CI.
 *
 * Codes mirror `tmsis/students/services/invoice_debt.py` (SKIP_*),
 * `access_block_notify.py` (SKIP_*), `models/access_block.py` (statuses).
 */

export const NS = 'pages.debtors.suspended';

/** A label the component resolves: an i18n key or ready text (a name). */
export type ILabel =
  | { key: string; values?: Record<string, string | number> }
  | { text: string };

export type ITone = 'ok' | 'warn' | 'danger' | 'neutral' | 'info';

type TFn = (key: string, values?: Record<string, string | number>) => string;

/** Resolve a descriptor with the component's `t`. */
export const labelText = (label: ILabel, t: TFn): string =>
  'text' in label ? label.text : String(t(label.key, label.values));

/** An invoice a suspension is (or was) tied to — `LmsBlockInvoiceType`. */
export interface ILmsBlockInvoice {
  invoiceId?: string | null;
  number?: string | null;
  amount?: number | null;
  currency?: string | null;
  paymentTo?: string | null;
  daysOverdue: number;
  viewUrl?: string | null;
  paymentUrl?: string | null;
  /** Null on history snapshots (paid-ness was not recorded then). */
  isPaid?: boolean | null;
}

/** One currency's amount — currencies are never summed. */
export interface ILmsAmountDue {
  amount: number;
  currency: string;
}

/** `LmsBlockNotificationType`. */
export interface ILmsBlockNotification {
  id: string;
  channel: string;
  kind: string;
  status: string;
  skipReason?: string | null;
  error?: string | null;
  sentAt?: string | null;
  createdAt?: string | null;
}

// ---------------------------------------------------------------------------
// List status (the segment switch)
// ---------------------------------------------------------------------------

export const LMS_SUSPENSION_STATUSES = [
  'active',
  'history',
  'skipped',
] as const;
export type LmsSuspensionStatus = typeof LMS_SUSPENSION_STATUSES[number];

/** URL value → status; anything unknown falls back to «active». */
export const parseSuspensionStatus = (
  raw: string | null | undefined,
): LmsSuspensionStatus =>
  (LMS_SUSPENSION_STATUSES as readonly string[]).includes(raw || '')
    ? (raw as LmsSuspensionStatus)
    : 'active';

export const SUSPENSION_STATUS_KEYS: Record<LmsSuspensionStatus, string> = {
  active: `${NS}.status.active`,
  history: `${NS}.status.history`,
  skipped: `${NS}.status.skipped`,
};

// ---------------------------------------------------------------------------
// Why the robot left a student alone (skipped rows)
// ---------------------------------------------------------------------------

export const SKIP_REASON_KEYS: Record<string, string> = {
  waiting_dofinansowanie: `${NS}.skipReason.waiting_dofinansowanie`,
  waived: `${NS}.skipReason.waived`,
};

export const skipReasonLabel = (code: string | null | undefined): ILabel => {
  if (!code) return { text: '—' };
  const key = SKIP_REASON_KEYS[code];
  return key ? { key } : { key: `${NS}.skipReason.unknown`, values: { code } };
};

// ---------------------------------------------------------------------------
// Who suspended / restored
// ---------------------------------------------------------------------------

export const ACTOR_KEYS = {
  robot: `${NS}.actor.robot`,
  staff: `${NS}.actor.staff`,
  legacy: `${NS}.actor.legacy`,
} as const;

/**
 * `robot` → «Robot» (even if a name leaked in); `legacy` — a block set before
 * ТЗ 0074 went live, which the robot will not lift on its own → «before the
 * automation — check it»; a person's name when the server gave one; a manual
 * action without a name → «сотрудник»; nothing at all → dash.
 */
export const actorLabel = (
  source: string | null | undefined,
  name: string | null | undefined,
): ILabel => {
  if (source === 'robot') return { key: ACTOR_KEYS.robot };
  if (source === 'legacy') return { key: ACTOR_KEYS.legacy };
  if (name && name.trim()) return { text: name.trim() };
  if (source === 'manual') return { key: ACTOR_KEYS.staff };
  return { text: '—' };
};

/**
 * A legacy block needs a human look, so it is the one source drawn as a
 * warning among the active rows; the others are plain.
 */
export const sourceTone = (source: string | null | undefined): ITone =>
  source === 'legacy' ? 'warn' : source === 'robot' ? 'info' : 'neutral';

// ---------------------------------------------------------------------------
// Notifications (email / Slack)
// ---------------------------------------------------------------------------

export const NOTIFICATION_CHANNEL_KEYS: Record<string, string> = {
  email: `${NS}.notify.channel.email`,
  slack: `${NS}.notify.channel.slack`,
  // ТЗ 0081: SMS студенту о приостановке и возврате.
  sms: `${NS}.notify.channel.sms`,
};

export const NOTIFICATION_KIND_KEYS: Record<string, string> = {
  suspended: `${NS}.notify.kind.suspended`,
  restored: `${NS}.notify.kind.restored`,
};

export const NOTIFICATION_STATUS_KEYS: Record<string, string> = {
  pending: `${NS}.notify.status.pending`,
  sending: `${NS}.notify.status.sending`,
  sent: `${NS}.notify.status.sent`,
  skipped: `${NS}.notify.status.skipped`,
  failed: `${NS}.notify.status.failed`,
};

const NOTIFICATION_STATUS_TONES: Record<string, ITone> = {
  pending: 'info',
  sending: 'info',
  sent: 'ok',
  skipped: 'warn',
  failed: 'danger',
};

export const notificationStatusTone = (
  status: string | null | undefined,
): ITone => NOTIFICATION_STATUS_TONES[status || ''] || 'neutral';

export const notificationStatusLabel = (
  status: string | null | undefined,
): ILabel => {
  const key = NOTIFICATION_STATUS_KEYS[status || ''];
  return key ? { key } : { text: status || '—' };
};

export const channelLabel = (channel: string | null | undefined): ILabel => {
  const key = NOTIFICATION_CHANNEL_KEYS[channel || ''];
  return key ? { key } : { text: channel || '—' };
};

export const kindLabel = (kind: string | null | undefined): ILabel => {
  const key = NOTIFICATION_KIND_KEYS[kind || ''];
  return key ? { key } : { text: kind || '—' };
};

/**
 * `disabled` — the channel is switched off in the ERP settings (ТЗ 0074
 * доработка); `disabled_on_server` — its env master switch is off.
 */
export const NOTIFICATION_SKIP_REASON_KEYS: Record<string, string> = {
  disabled: `${NS}.notify.skip.disabled`,
  disabled_on_server: `${NS}.notify.skip.disabled_on_server`,
  no_template: `${NS}.notify.skip.no_template`,
  no_email: `${NS}.notify.skip.no_email`,
  no_slack_link: `${NS}.notify.skip.no_slack_link`,
  no_bot_token: `${NS}.notify.skip.no_bot_token`,
  missing_scope: `${NS}.notify.skip.missing_scope`,
  unknown_channel: `${NS}.notify.skip.unknown_channel`,
  superseded: `${NS}.notify.skip.superseded`,
  stale: `${NS}.notify.skip.stale`,
  // ТЗ 0081: причины пропуска SMS (`outreach/constants.py`).
  entity_not_allowed: `${NS}.notify.skip.entity_not_allowed`,
  no_phone: `${NS}.notify.skip.no_phone`,
  invalid_phone: `${NS}.notify.skip.invalid_phone`,
  not_allowlisted: `${NS}.notify.skip.not_allowlisted`,
  foreign_number: `${NS}.notify.skip.foreign_number`,
  opted_out: `${NS}.notify.skip.opted_out`,
  closed: `${NS}.notify.skip.closed`,
  daily_cap: `${NS}.notify.skip.daily_cap`,
  too_long: `${NS}.notify.skip.too_long`,
  links_not_allowed: `${NS}.notify.skip.links_not_allowed`,
  cancelled: `${NS}.notify.skip.cancelled`,
  refresh_failed: `${NS}.notify.skip.refresh_failed`,
  // Перепроверка 0084: письмо о доступе в LMS со ссылкой — только короткой;
  // сократитель не настроен — письмо не уходит.
  shortlink_disabled: `${NS}.notify.skip.shortlink_disabled`,
};

/**
 * The Slack channel joins one reason per workspace with commas
 * (`no_bot_token,missing_scope`). Each known code gets its sentence; an
 * unknown one is shown raw rather than swallowed.
 */
export const notificationSkipReasonLabels = (
  raw: string | null | undefined,
): ILabel[] =>
  (raw || '')
    .split(',')
    .map((code) => code.trim())
    .filter(Boolean)
    .map((code) => {
      const key = NOTIFICATION_SKIP_REASON_KEYS[code];
      return key ? { key } : { text: code };
    });

// ---------------------------------------------------------------------------
// The robot's last run
// ---------------------------------------------------------------------------

export const ROBOT_RUN_STATUS_KEYS: Record<string, string> = {
  running: `${NS}.robot.status.running`,
  success: `${NS}.robot.status.success`,
  failed: `${NS}.robot.status.failed`,
};

export const robotRunTone = (status: string | null | undefined): ITone =>
  status === 'success' ? 'ok' : status === 'failed' ? 'danger' : 'info';

export interface IRobotRunCounts {
  suspended: number;
  restored: number;
  skipped: number;
  refreshFailed: number;
  candidates: number;
  /** Pre-0074 blocks without invoices the robot left for a manual check. */
  legacyWithoutInvoices: number;
}

const num = (value: unknown): number => {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
};

/**
 * `stats` is a graphene JSONString — a JSON **string** on the wire, but an
 * object if a cache layer already parsed it. Anything unparseable → `{}`.
 */
export const parseRobotStats = (raw: unknown): Record<string, unknown> => {
  if (!raw) return {};
  if (typeof raw === 'object') return raw as Record<string, unknown>;
  if (typeof raw !== 'string') return {};
  try {
    const parsed = JSON.parse(raw);
    // Double-encoded JSONString (a JSON string holding JSON) — unwrap once.
    if (typeof parsed === 'string') return parseRobotStats(parsed);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed
      : {};
  } catch {
    return {};
  }
};

export const robotRunCounts = (raw: unknown): IRobotRunCounts => {
  const stats = parseRobotStats(raw);
  return {
    suspended: num(stats.suspended),
    restored: num(stats.restored),
    skipped: num(stats.skipped_dofinansowanie) + num(stats.skipped_waived),
    refreshFailed: num(stats.refresh_failed),
    candidates: num(stats.candidates),
    legacyWithoutInvoices: num(stats.legacy_without_invoices),
  };
};

// ---------------------------------------------------------------------------
// Mutations and paging
// ---------------------------------------------------------------------------

export interface ILmsBlockVariables {
  studentId: string;
  reason: string | null;
  notifyStudent: boolean;
}

/**
 * Variables for `IssueLmsBlock` / `LiftLmsBlock`. `notifyStudent` defaults to
 * TRUE (ТЗ 0074 §3.4) — only an explicit `false` silences the email/Slack.
 */
export const buildLmsBlockVariables = (
  studentId: string,
  options: { reason?: string | null; notifyStudent?: boolean } = {},
): ILmsBlockVariables => ({
  studentId,
  reason: (options.reason || '').trim() || null,
  notifyStudent: options.notifyStudent !== false,
});

/**
 * The offset to show after one row left the current page (a restore in the
 * «active» list). The same page is re-read with its own variables; only when
 * that page would now be empty does the view step back one page.
 */
export const offsetAfterRemoval = (
  offset: number,
  limit: number,
  total: number,
): number => {
  const remaining = Math.max(total - 1, 0);
  if (offset > 0 && offset >= remaining) {
    return Math.max(offset - limit, 0);
  }
  return offset;
};

/** Every static i18n key this module can hand out — walked by the locale gate. */
export const LMS_SUSPENSION_I18N_KEYS: string[] = [
  ...Object.values(SUSPENSION_STATUS_KEYS),
  ...Object.values(SKIP_REASON_KEYS),
  `${NS}.skipReason.unknown`,
  ...Object.values(ACTOR_KEYS),
  ...Object.values(NOTIFICATION_CHANNEL_KEYS),
  ...Object.values(NOTIFICATION_KIND_KEYS),
  ...Object.values(NOTIFICATION_STATUS_KEYS),
  ...Object.values(NOTIFICATION_SKIP_REASON_KEYS),
  ...Object.values(ROBOT_RUN_STATUS_KEYS),
];
